import axios from 'axios'
import {createAsyncThunk, PayloadAction, createAction, Action} from '@reduxjs/toolkit'
import { sliceStateStatus, systemStatus } from '@/Core';
import {routesIndex} from '@/RoutesIndex';

import ISliceState from '@/interface/core/ISliceState';
import { generatePaginationStateDataItem, generateSmrStateDataItem } from '../../../hooks'; 
import ISingleMembershipRequest from '@/interface/ISingleMembershipRequest';
import IPagination from '@/interface/core/IPagination';
import { IType } from '@/interface/IType';

/**
 * Richieste online di iscrizione registrate
 * Recupera le richieste singole di iscrizione per anno.
 *
 * @param {string | number} anno - Anno di riferimento 
 */

export const smrIndex = createAsyncThunk(
  'smr/index',
  async (anno: string, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        params: { anno }
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + routesIndex.api.methods.index;

      const q = axios.get(uri, config);
      const r = (await q).data;

      return setStateData(r);

    }
    catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Conta le richieste online singole non approvate
 * @param {string | number} anno - Anno di riferimento (opzionale)
 */
export const smrCountPending = createAsyncThunk(
  'smr/countPending',
  async (anno: number | null = null, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + routesIndex.api.methods.count;

      const payload = anno ? { anno } : {};

      const response = await axios.post(uri, payload, config);

      return {
        count: response.data.count,
        anno: anno || response.data.anno || null
      };
      
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


/**
 * Richieste online di iscrizione registrate
 * Risultato paginato
 *
 * @param {string | number} anno - Anno di riferimento
 * @param {number} pageSize - Numero risultati per pagina
 * @param {string} globalFilter - Filtro su nome/cognome
 */

export const smrPaginate = createAsyncThunk(
  'smr/paginate',
  async (_data: any, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      };

      let uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + routesIndex.api.methods.paginate;
      if (_data!.page && parseInt(_data!.page) > 1) {
        uri += '?page=' + _data!.page;
      }

      // const start = performance.now();
            
      const q = axios.post(uri, _data, config);
      const r = (await q).data;

      // const end = performance.now();
      // console.log(`⏱️ Tempo totale api: ${((end - start) / 1000).toFixed(2)} secondi`);

      return setStateData(r, true); 
    }
    catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


/**
 * Visualizza una singola richiesta online di iscrizione
 *
 * @param {string | number} id - ID della richiesta
 */
export const smrView = createAsyncThunk(
  'smr/view',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + '/' + id;

      const response = await axios.get(uri, config);

      return setStateData(response.data); 
      
    } catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

/**
 * Attiva una richiesta online di iscrizione singola
 * @param {any} payload - Dati della richiesta da attivare
 */
export const smrActivate = createAsyncThunk(
  'smr/activate',
  async (payload: any, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + routesIndex.api.methods.activate;
      const response = await axios.post(uri, payload, config);

      return response.data.result;

    } catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

/**
 * Aggiorna solo i dati della richiesta/tesserato senza creare tessera (solo modifica).
 * Usato quando si salva dalla modale aperta con click sulla riga (non sul bottone Attiva).
 */
export const smrUpdate = createAsyncThunk(
  'smr/update',
  async (payload: any, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + routesIndex.api.methods.update;
      const response = await axios.post(uri, payload, config);

      return response.data.result;

    } catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

/**
 * Rifiuta una richiesta online di iscrizione singola
 * @param {number} id - ID della richiesta da rifiutare
 */
export const smrRefuse = createAsyncThunk(
  'smr/refuse',
  async (id: number, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix + routesIndex.api.singleMembershipRequests + '/' + id;;
      const response = await axios.delete(uri, config);

      return setStateData(response.data);

    } catch (error: any) {
      if (error.response && error.response.data.result) {
        return rejectWithValue(error.response.data.result);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);






const setStateData = (apiResponse: any, paginate: boolean = false) => {
  let stateData: ISliceState = {
    data: [],
    status: sliceStateStatus.fulfilled,
  };

  if (apiResponse.status && apiResponse.result) {
    if (paginate) {
      stateData.data = {} as IPagination;
      Object.assign(stateData.data, generatePaginationStateDataItem(apiResponse.result, IType.smr)); 
    } else {
      if (Array.isArray(apiResponse.result)) {
        stateData.data = apiResponse.result.map((item: any) =>
          generateSmrStateDataItem(item) 
        );
      } else {
        stateData.data = generateSmrStateDataItem(apiResponse.result);
      }
    }
  }

  return stateData;
};